import { useMemo } from 'react';
import { useUnifiedSearch } from '../../api/useUnifiedSearch';
import type { UnifiedSearchRequest } from '../../api/unifiedSearchTypes';
import { candidatesFromSearch } from '../model/fromAssetDetail';
import type { CandidateEvent } from '../model/types';

export const CANDIDATE_LIMIT = 50;

export interface CandidateEvents {
    events: CandidateEvent[];
    loading: boolean;
    loaded: boolean;
    error: boolean;
    closedLoading: boolean;
    closedLoaded: boolean;
}

export function candidateRequest(
    ticker: string | null,
    assetType: string | null,
    withClosed: boolean
): UnifiedSearchRequest | null {
    if (!ticker || !assetType) return null;
    return {
        limit: CANDIDATE_LIMIT,
        include_assets: false,
        include_active_events: true,
        include_closed_events: withClosed,
        asset_context: ticker,
        asset_context_type: assetType,
    };
}

export function useCandidateEvents(
    ticker: string | null,
    assetType: string | null,
    withClosed: boolean
): CandidateEvents {
    const openParams = useMemo(
        () => candidateRequest(ticker, assetType, false),
        [ticker, assetType]
    );
    const closedParams = useMemo(
        () => (withClosed ? candidateRequest(ticker, assetType, true) : null),
        [ticker, assetType, withClosed]
    );
    const open = useUnifiedSearch(openParams);
    const closed = useUnifiedSearch(closedParams);

    const events = useMemo(
        () =>
            candidatesFromSearch(
                open.data?.active_events ?? [],
                closed.data?.closed_events ?? []
            ),
        [open.data, closed.data]
    );
    return {
        events,
        loading: open.loading,
        loaded: open.data !== null,
        error: open.error !== null,
        closedLoading: closed.loading,
        closedLoaded: closed.data !== null,
    };
}
